import { useEffect, useState } from "react";
import { ScrollReveal } from "@/components/ScrollReveal";
import { api, resolveImage } from "@/lib/api";
import { ArrowUpRight, FlaskConical } from "lucide-react";

const spanClass = (size) => {
  if (size === "large") return "lg:col-span-8";
  if (size === "medium") return "lg:col-span-6";
  return "lg:col-span-4";
};

export const HealthPackages = () => {
  const [packages, setPackages] = useState([]);

  useEffect(() => {
    api
      .get("/packages")
      .then(({ data }) => setPackages(data))
      .catch(() => setPackages([]));
  }, []);

  if (packages.length === 0) return null;

  return (
    <section id="packages" className="relative bg-bone py-24 lg:py-32 overflow-hidden" data-testid="packages-section">
      <div className="absolute inset-0 dot-grid opacity-70 pointer-events-none" />
      <div className="absolute inset-0 glow-soft pointer-events-none" />
      <div className="max-w-[1440px] mx-auto px-6 lg:px-12 relative">
        <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-6 mb-14">
          <ScrollReveal>
            <span className="text-teal text-xs uppercase tracking-[0.3em] font-semibold">
              Health Packages
            </span>
            <h2 className="font-display text-4xl lg:text-5xl text-navy mt-3 max-w-xl">
              Curated checkups for real Indian lives.
            </h2>
          </ScrollReveal>
          <ScrollReveal delay={0.1}>
            <p className="text-slate-600 max-w-sm text-sm leading-relaxed">
              Every package is designed with our specialists — not generic
              templates — so the tests actually match the risks that matter.
            </p>
          </ScrollReveal>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
          {packages.map((pkg, i) => (
            <ScrollReveal
              key={pkg.id || pkg.slug}
              delay={(i % 3) * 0.08}
              className={`col-span-1 ${spanClass(pkg.size)}`}
            >
              <div
                data-testid={`package-card-${pkg.slug}`}
                className="group card-lift top-rule relative h-full bg-white border border-navy/8 hover:border-teal/30 flex flex-col justify-between min-h-[260px] overflow-hidden shadow-[0_8px_30px_rgba(15,31,61,0.04)] hover:shadow-[0_28px_60px_rgba(15,31,61,0.12)]"
              >
                {pkg.image && (
                  <div className="absolute inset-0 overflow-hidden">
                    <img
                      src={resolveImage(pkg.image)}
                      alt={pkg.name}
                      className="w-full h-full object-cover opacity-[0.14] group-hover:scale-105 group-hover:opacity-20 transition-all duration-500"
                    />
                  </div>
                )}
                <div className="relative z-10 p-8 lg:p-10 flex flex-col justify-between h-full">
                  {pkg.tag && (
                    <span className="absolute top-8 right-8 lg:top-10 lg:right-10 text-[10px] uppercase tracking-[0.2em] text-teal border border-teal/40 px-2 py-1">
                      {pkg.tag}
                    </span>
                  )}
                  <FlaskConical size={22} className="text-teal mb-6 group-hover:rotate-[-8deg] group-hover:scale-110 transition-transform duration-500" strokeWidth={1.5} />
                  <div>
                    <h3 className="font-display text-2xl lg:text-3xl leading-tight mb-3 text-navy">
                      {pkg.name}
                    </h3>
                    <p className="text-slate-600 text-sm leading-relaxed mb-6 max-w-md">
                      {pkg.desc}
                    </p>
                    <div className="flex items-center justify-between">
                      <span className="text-xs uppercase tracking-[0.2em] text-teal font-medium">
                        {pkg.params}
                      </span>
                      <a
                        href="#book"
                        data-testid={`package-book-${pkg.slug}`}
                        className="flex items-center gap-1 text-xs uppercase tracking-[0.2em] text-navy group-hover:text-teal transition-colors duration-300"
                      >
                        Book <ArrowUpRight size={14} className="group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform duration-300" />
                      </a>
                    </div>
                  </div>
                </div>
              </div>
            </ScrollReveal>
          ))}
        </div>
      </div>
    </section>
  );
};
